class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartClearButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      this.clearCart();
    });
  }

  clearCart() {
    const button = this.querySelector('button');
    if (button) {
      button.setAttribute('disabled', 'disabled');
      button.classList.add('loading');
      const spinner = button.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');
    }

    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      const mainCartItems = document.getElementById('CartDrawer-CartItems');
      if (mainCartItems) {
        mainCartItems.classList.add('cart__items--disabled');
      }
      const spinners = cartDrawerItems.querySelectorAll('.loading__spinner');
      spinners.forEach(spinner => spinner.classList.remove('hidden'));
    }

    const clearUrl = window.routes.cart_clear_url || '/cart/clear.js';

    fetch(clearUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then((cartData) => {
      const cartUrl = window.routes.cart_url || '/cart';
      return fetch(`${cartUrl}?sections=cart-drawer,cart-icon-bubble`);
    })
    .then((response) => {
      if (!response.ok) {
        throw new Error('Failed to fetch updated sections');
      }
      return response.json();
    })
    .then((sections) => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.classList.add('is-empty');
        const drawerInner = cartDrawer.querySelector('.drawer__inner');
        if (drawerInner && sections['cart-drawer']) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
          const newInner = doc.querySelector('.drawer__inner');
          if (newInner) {
            drawerInner.innerHTML = newInner.innerHTML;
          }
        }
      }

      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && sections['cart-icon-bubble']) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newBubble = doc.querySelector('#cart-icon-bubble');
        if (newBubble) {
          cartIconBubble.innerHTML = newBubble.innerHTML;
        }
      }

      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) {
        cartFooter.classList.add('is-empty');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-clear' });
      }

      const cartUpdatedEvent = new CustomEvent('cartUpdated', {
        detail: {
          message: 'Cart was updated',
        }
      });
      document.dispatchEvent(cartUpdatedEvent);
    })
    .catch((error) => {
      console.error('Error clearing cart:', error);
      if (button) {
        button.removeAttribute('disabled');
        button.classList.remove('loading');
        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }
      if (cartDrawerItems) {
        const mainCartItems = document.getElementById('CartDrawer-CartItems');
        if (mainCartItems) {
          mainCartItems.classList.remove('cart__items--disabled');
        }
        const spinners = cartDrawerItems.querySelectorAll('.loading__spinner');
        spinners.forEach(spinner => spinner.classList.add('hidden'));
      }
    });
  }
}

customElements.define('cart-clear-button', CartClearButton);

